/**
 * Compartilhar um texto com outra conta.
 *
 * O compartilhamento é **nominal**: a pessoa é encontrada pelo e-mail da conta que
 * ela já tem aqui, e o acesso vale para ela. Não existe link público — texto de
 * trabalho, sermão ou ata não fica aberto na internet sem o dono saber com quem
 * está.
 *
 * Dois níveis, e a diferença é grande o bastante para ficar visível na tela:
 * **ler** (abre, imprime, baixa — não altera) e **editar** (escreve no texto).
 */

import { useCallback, useEffect, useState } from 'react';
import { Loader2, Share2, Trash2, TriangleAlert, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Campo } from '@/components/ui/label';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import { api, ErroApi, type CompartilhamentoDeTexto } from '@/lib/api';

export function CompartilharTexto({ textoId }: { textoId: string }) {
  const [aberto, setAberto] = useState(false);
  const [lista, setLista] = useState<CompartilhamentoDeTexto[] | null>(null);
  const [email, setEmail] = useState('');
  const [podeEditar, setPodeEditar] = useState(false);
  const [ocupado, setOcupado] = useState('');
  const [erro, setErro] = useState('');

  const carregar = useCallback(async () => {
    try {
      const { compartilhamentos } = await api.listarCompartilhamentos(textoId);
      setLista(compartilhamentos);
      setErro('');
    } catch (causa) {
      setLista([]);
      setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível carregar quem tem acesso.');
    }
  }, [textoId]);

  useEffect(() => {
    if (aberto) void carregar();
  }, [aberto, carregar]);

  const convidar = async () => {
    setOcupado('convidar');
    setErro('');
    try {
      await api.compartilharTexto(textoId, { email: email.trim(), podeEditar });
      setEmail('');
      setPodeEditar(false);
      await carregar();
    } catch (causa) {
      setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível compartilhar.');
    } finally {
      setOcupado('');
    }
  };

  const remover = async (usuarioId: string) => {
    setOcupado(usuarioId);
    try {
      await api.removerCompartilhamento(textoId, usuarioId);
      await carregar();
    } catch (causa) {
      setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível remover o acesso.');
    } finally {
      setOcupado('');
    }
  };

  return (
    <Dialog open={aberto} onOpenChange={setAberto}>
      <DialogTrigger asChild>
        <Button variant="contorno" tamanho="pequeno">
          <Share2 />
          Compartilhar
        </Button>
      </DialogTrigger>
      <DialogContent
        titulo="Compartilhar este texto"
        descricao="A pessoa precisa ter conta aqui e o módulo de textos liberado. O acesso vale só para ela — não é um link público."
      >
        <div className="space-y-3">
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-[220px] flex-1">
              <Campo rotulo="E-mail da conta">
                <input
                  type="email"
                  value={email}
                  onChange={(evento) => setEmail(evento.target.value)}
                  placeholder="pessoa@email.com"
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                />
              </Campo>
            </div>
            <Button onClick={() => void convidar()} disabled={ocupado === 'convidar' || !email.trim()}>
              {ocupado === 'convidar' ? <Loader2 className="animate-spin" /> : <UserPlus />}
              Dar acesso
            </Button>
          </div>

          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={podeEditar} onChange={(evento) => setPodeEditar(evento.target.checked)} />
            Pode <strong>editar</strong> o texto (sem marcar, só lê)
          </label>

          {erro ? (
            <p className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive">
              <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
              {erro}
            </p>
          ) : null}

          <div className="space-y-1">
            <p className="text-xs font-medium">Com acesso</p>
            {lista === null ? (
              <p className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="size-3.5 animate-spin" />
                Carregando...
              </p>
            ) : lista.length === 0 ? (
              <p className="text-xs text-muted-foreground">Só você tem acesso a este texto.</p>
            ) : (
              <ul className="divide-y divide-border rounded-md border border-border">
                {lista.map((item) => (
                  <li key={item.usuarioId} className="flex items-center gap-2 p-2 text-xs">
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium">{item.nome || item.email}</span>
                      <span className="block truncate text-muted-foreground">
                        {item.email} · {item.podeEditar ? 'pode editar' : 'só lê'}
                      </span>
                    </span>
                    <Button
                      variant="fantasma"
                      tamanho="pequeno"
                      onClick={() => void remover(item.usuarioId)}
                      disabled={ocupado === item.usuarioId}
                    >
                      <Trash2 />
                      Remover
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
